import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export const buttonVariants = cva(
  'relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold select-none transition-[background,color,transform,box-shadow,border-color] duration-200 ease-[var(--ease-out-soft)] active:scale-[0.97] disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-pink text-on-pink hover:bg-pink-hover',
        secondary:
          'bg-white/10 text-text hover:bg-white/16 backdrop-blur-md border border-white/10',
        ghost: 'text-muted hover:text-text hover:bg-white/6',
        outline: 'border border-line text-text hover:border-pink/60 hover:bg-white/4',
        danger: 'bg-danger/15 text-danger hover:bg-danger/25 border border-danger/30',
      },
      size: {
        sm: 'h-9 px-3.5 text-sm rounded-xl',
        md: 'h-11 px-5 text-[15px] rounded-xl',
        lg: 'h-13 px-7 text-base rounded-2xl',
        icon: 'h-10 w-10 rounded-full',
        'icon-lg': 'h-12 w-12 rounded-full',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, children, disabled, type = 'button', ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading && <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
});
